/**
 * @file HeaderWrapper.tsx
 * @module _admin/layout/components/header/HeaderWrapper
 * @description Renders the HeaderWrapper layout component that builds the shared UTrade Admin header and navigation.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

/* eslint-disable react-hooks/exhaustive-deps */
import clsx from "clsx";
import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { KTSVG, toAbsoluteUrl } from "../../../helpers";
import { error as showErrorToast } from "../../../../Global/toast";
import { useLayout } from "../../core";
import { getEnvironmentIndicatorClass } from "../../core/environmentIndicator";
import { Header } from "./Header";
import { Navbar } from "./Navbar";
import ImageWithFallback from "../../../../app/modules/_common/components/image-with-fallback";

export function HeaderWrapper() {
  const { config, classes } = useLayout();
  const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV;
  const [searchQuery, setSearchQuery] = useState("");
  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      return;
    }
    showErrorToast("Something went wrong. Please try again.");
  };
  if (!config.app?.header?.display) {
    return null;
  }

  return (
    <div
      id="kt_app_header"
      className={clsx(
        "app-header",
        getEnvironmentIndicatorClass(environment)
      )}
    >
      <div
        id="kt_app_header_container"
        className={clsx(
          "app-container w-100 flex-lg-grow-1",
          classes.headerContainer.join(" "),
          config.app?.header?.default?.containerClass
        )}
      >
        {config.app.sidebar?.display && (
          <>
            <div
              className="d-flex align-items-center d-lg-none ms-n2 me-2"
              title="Show sidebar menu"
            >
              <div
                className="btn btn-icon btn-active-color-primary w-35px h-35px"
                id="kt_app_sidebar_mobile_toggle"
              >
                <KTSVG
                  path="/media/icons/duotune/abstract/abs015.svg"
                  className=" svg-icon-1"
                />
              </div>
              <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
                <Link to="/dashboard" className="d-lg-none">
                  <img
                    alt="Logo"
                    src={toAbsoluteUrl("/media/sidebar.png")}
                    className="h-30px"
                  />
                </Link>
              </div>
            </div>
          </>
        )}

        {!config.app.sidebar?.display && (
          <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0 me-lg-15">
            <Link to="/dashboard">
              {config.layoutType !== "dark-header" ? (
                <ImageWithFallback
                  alt="Logo"
                  src={toAbsoluteUrl("/media/logos/default-small-dark.svg")}
                  className="h-20px h-lg-30px app-sidebar-logo-default"
                  noDefaultSize
                />
              ) : (
                <>
                  <ImageWithFallback
                    alt="Logo"
                    src={toAbsoluteUrl("/media/logos/default-small.svg")}
                    className="h-20px h-lg-30px app-sidebar-logo-default theme-light-show"
                    noDefaultSize
                  />
                  <ImageWithFallback
                    alt="Logo"
                    src={toAbsoluteUrl("/media/logos/default-small-dark.svg")}
                    className="h-20px h-lg-30px app-sidebar-logo-default theme-dark-show"
                    noDefaultSize
                  />
                </>
              )}
            </Link>
          </div>
        )}

        <div className="d-none d-md-flex align-items-center flex-grow-1 mx-4">
          <form
            className="w-100"
            style={{ maxWidth: "480px" }}
            onSubmit={handleSearchSubmit}
          >
            <div className="position-relative">
              <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted"></i>
              <input
                type="text"
                className="form-control ps-10"
                placeholder="Search orders, customers, SKUs, suppliers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>
        </div>

        <div
          id="kt_app_header_wrapper"
          className="d-flex align-items-stretch justify-content-end flex-lg-grow-1"
        >
          {config.app.header.default?.content === "menu" &&
            config.app.header.default.menu?.display && (
              <div
                className="app-header-menu app-header-mobile-drawer align-items-stretch"
                data-kt-drawer="true"
                data-kt-drawer-name="app-header-menu"
                data-kt-drawer-activate="{default: true, lg: false}"
                data-kt-drawer-overlay="true"
                data-kt-drawer-width="225px"
                data-kt-drawer-direction="end"
                data-kt-drawer-toggle="#kt_app_header_menu_toggle"
                data-kt-swapper="true"
                data-kt-swapper-mode="{default: 'append', lg: 'prepend'}"
                data-kt-swapper-parent="{default: '#kt_app_body', lg: '#kt_app_header_wrapper'}"
              >
                <Header />
              </div>
            )}
          <Navbar />
        </div>
      </div>
    </div>
  );
}
